import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search, RotateCcw, Save, X, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, AlertTriangle, FileSpreadsheet,
  ZoomIn, ZoomOut, Maximize2, Cloud, CloudOff,
} from 'lucide-react';

// ──────────────────────────────────────────────────────────
// 운영 doc_type 11종 (EvalLabelingModal 과 동일 코드 체계)
// 색은 최소화 — 활성 탭만 짙게, 비활성은 회색
// ──────────────────────────────────────────────────────────
const DOC_TYPES = [
  { code: 'ALL',     name: '전체' },
  { code: 'B',       name: '말소' },
  { code: 'I',       name: '등록증' },
  { code: 'D',       name: '폐차' },
  { code: 'L',       name: '등록원부' },
  { code: 'N',       name: '경락' },
  { code: 'BIZ',     name: '사업자' },
  { code: 'RID',     name: '주민증' },
  { code: 'CONT',    name: '매매계약서' },
  { code: 'SHRG',    name: '쇼링내역서' },
  { code: 'TAX',     name: '세금계산서' },
  { code: 'INVOICE', name: '인보이스' },
];

const FIELD_LABELS = {
  vin: '차대번호 (VIN, 17자)',
  plate_no: '자동차등록번호',
  car_name: '차명 (영문)',
  maker: '제조사',
  year: '모델연도 (4자리)',
  mileage_km: '주행거리 (km)',
  owner_name: '소유자명',
  owner_id_no: '주민/법인번호',
  owner_address: '사용본거지/주소',
  displacement_cc: '배기량 (cc)',
  vehicle_weight_kg: '총중량 (kg)',
  length_cm: '길이 (mm)',
  width_cm: '너비 (mm)',
  height_cm: '높이 (mm)',
  canceled_date: '말소등록일',
  registration_date: '최초등록일',
  doc_type: '문서유형',
  seller_name: '판매자',
  buyer_name: '구매자',
  invoice_no: '인보이스번호',
  blno: 'B/L No',
  fob_price: 'FOB',
  net_weight: 'Net',
  gross_weight: 'Gross',
  package: 'PKG',
};
const fieldLabel = (k) => FIELD_LABELS[k] || k;

const MATCH_FILTERS = [
  { value: '',             label: '전체' },
  { value: 'full',         label: '완전일치' },
  { value: 'partial',      label: '부분일치' },
  { value: 'none',         label: '미일치' },
  { value: 'ocr_partial',  label: 'OCR 부분(마스킹)' },
  { value: 'vin_invalid',  label: 'VIN 형식불량' },
];
const SPLIT_FILTERS  = [{ value: 'all', label: '전체' }, { value: 'val', label: 'val' }, { value: 'train', label: 'train' }];
const ADAPTER_FILTERS = [{ value: 'all', label: '어댑터 전체' }, { value: 'vision', label: 'vision' }, { value: 'text', label: 'text' }];

const pct = (x) => (x == null ? '-' : (x * 100).toFixed(1) + '%');

// 매칭 dot — 일치는 sky 액센트, 나머지는 회색/rose
const dotColor = (s) =>
  s === true ? 'bg-blue-500'
  : s === 'ocr_partial' ? 'bg-blue-300'
  : s === false ? 'bg-rose-400'
  : 'bg-gray-200';

function flatten(obj) {
  const out = {};
  if (!obj || typeof obj !== 'object') return out;
  for (const [k, v] of Object.entries(obj)) {
    if (k === 'extra' && v && typeof v === 'object') {
      for (const [ek, ev] of Object.entries(v)) out[`extra.${ek}`] = ev;
    } else if (k === 'items') { /* skip arrays */ }
    else { out[k] = v; }
  }
  return out;
}

// ──────────────────────────────────────────────────────────
// 가운데 이미지 뷰어 — +/- 줌 + 페이지 네비게이션
// ──────────────────────────────────────────────────────────
function ImageViewer({ urls, sourceFilename, xlsxUrl, onClose }) {
  const [idx, setIdx] = useState(0);
  const [scale, setScale] = useState(1);
  const containerRef = useRef(null);
  useEffect(() => { setIdx(0); setScale(1); }, [urls?.[0]]);

  if ((!urls || urls.length === 0) && !xlsxUrl) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-gray-400 bg-gray-50">
        샘플을 선택하세요
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-gray-100 min-w-0">
      {/* 컨트롤 바 */}
      <div className="px-3 py-1.5 border-b bg-white flex items-center gap-2 text-xs">
        {urls && urls.length > 0 && (
          <>
            <button
              disabled={idx === 0}
              onClick={() => setIdx(i => Math.max(0, i - 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <span className="text-gray-700 tabular-nums">{idx + 1} / {urls.length}</span>
            <button
              disabled={idx >= urls.length - 1}
              onClick={() => setIdx(i => Math.min(urls.length - 1, i + 1))}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </>
        )}
        <div className="flex-1" />
        <button onClick={() => setScale(s => Math.max(0.25, +(s - 0.25).toFixed(2)))}
          className="p-1 rounded hover:bg-gray-100" title="축소">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="text-gray-700 tabular-nums w-12 text-center">{Math.round(scale * 100)}%</span>
        <button onClick={() => setScale(s => Math.min(5, +(s + 0.25).toFixed(2)))}
          className="p-1 rounded hover:bg-gray-100" title="확대">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setScale(1)} className="p-1 rounded hover:bg-gray-100" title="원본">
          <Maximize2 className="w-3.5 h-3.5" />
        </button>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" title="닫기">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* 본문 (스크롤 + 줌) */}
      <div ref={containerRef} className="flex-1 overflow-auto bg-gray-50 p-4">
        {urls && urls.length > 0 ? (
          <div className="flex justify-center">
            <img
              src={urls[idx]}
              alt=""
              style={{
                transform: `scale(${scale})`,
                transformOrigin: 'top center',
                maxWidth: scale <= 1 ? '100%' : 'none',
              }}
              className="bg-white shadow-sm transition-transform"
            />
          </div>
        ) : xlsxUrl ? (
          <div className="flex flex-col items-center justify-center h-full text-sm text-gray-600 gap-2">
            <FileSpreadsheet className="w-12 h-12 text-gray-400" />
            <a href={xlsxUrl} target="_blank" rel="noreferrer" className="text-gray-900 underline">
              {sourceFilename || 'xlsx 다운로드'}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// 우측 GT 편집 폼 — EvalLabelingModal 과 동일 톤
// ──────────────────────────────────────────────────────────
function FormPanel({ sample, onClose, onSaved, onClearSelection }) {
  const [editGt, setEditGt] = useState(null);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (sample) {
      setEditGt(JSON.parse(JSON.stringify(sample.gt || {})));
      setNotes('');
    }
  }, [sample?.id]);

  if (!sample) return null;

  const fr = sample.field_results || {};
  const frF = sample.field_results_filtered || {};
  const gtFlat = flatten(sample.gt || {});
  const predFlat = flatten(sample.pred_json || {});
  const predFlatF = flatten(sample.pred_json_filtered || {});

  const allKeys = Array.from(new Set([
    ...Object.keys(gtFlat),
    ...Object.keys(predFlat),
  ]));
  const editFlat = flatten(editGt || {});

  const setEditField = (key, val) => {
    const next = { ...(editGt || {}) };
    if (key.startsWith('extra.')) {
      next.extra = { ...(next.extra || {}), [key.slice(6)]: val };
    } else {
      next[key] = val;
    }
    setEditGt(next);
  };

  const copyPredToGt = (key) => {
    const v = predFlat[key];
    if (v != null) setEditField(key, String(v));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`/api/export-eval/samples/${encodeURIComponent(sample.id)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gt: editGt, notes }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'save failed');
      onSaved && onSaved(j);
      const bucket = j.adapter_type || 'vision';
      const cnt = bucket === 'text' ? j.text_count : j.vision_count;
      alert(`저장 완료. ${bucket} 누적 ${cnt}건 (변경 필드 ${j.changed_count})`);
    } catch (e) {
      alert(`저장 실패: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-[380px] flex-shrink-0 border-l bg-white flex flex-col">
      {/* 헤더 */}
      <div className="px-4 py-3 border-b">
        <div className="flex items-start justify-between mb-1.5">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-[11px] px-2 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
              {sample.doc_type_nm || sample.doc_type || '?'}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">
              {sample.split}
            </span>
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-600 border border-gray-200">
              {sample.adapter_type || 'vision'}
            </span>
          </div>
          <button onClick={onClearSelection} className="p-1 rounded hover:bg-gray-100 text-gray-500">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="text-xs font-mono text-gray-500 truncate mb-2">{sample.id}</div>

        {sample.has_pred ? (
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-gray-700">
            <span>일치 <b className="text-blue-700">{sample.match_count}</b>/{sample.total_fields}</span>
            <span>OCR <b className="text-gray-900">{sample.ocr_match_count}</b>/{sample.total_fields}</span>
            <span>필터 <b className="text-blue-700">{sample.match_count_filtered}</b>/{sample.total_fields}</span>
            {sample.vin_validation_pred && (
              <span className={sample.vin_validation_pred.valid_format ? 'text-blue-700' : 'text-rose-600 font-semibold'}>
                VIN {sample.vin_validation_pred.valid_format ? 'OK' : '불량'}
              </span>
            )}
          </div>
        ) : (
          <div className="text-xs text-gray-400">PRED 없음 (train 샘플)</div>
        )}
      </div>

      {/* 안내 */}
      <div className="px-4 py-2 border-b bg-gray-50">
        <div className="text-[11px] text-gray-600 leading-relaxed">
          양식에 보이는 값만 입력. PRED 값을 클릭하면 GT에 자동 복사됩니다.
        </div>
      </div>

      {/* 필드 폼 */}
      <div className="flex-1 overflow-auto px-4 py-3 space-y-2">
        {allKeys.length === 0 && (
          <div className="text-xs text-gray-400 text-center py-6">필드 없음</div>
        )}
        {allKeys.map(k => {
          const status = fr[k];
          const statusF = frF[k];
          const predV = predFlat[k];
          const predFV = predFlatF[k];
          const editV = editFlat[k];
          const showFilterDiff = predFlatF && Object.keys(predFlatF).length > 0 && predFV !== predV;

          return (
            <div key={k} className="grid grid-cols-12 gap-2 items-start">
              <label className="col-span-5 text-xs text-gray-700 leading-7 flex items-center gap-1.5 min-w-0">
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dotColor(status)}`} />
                <span className="truncate">{fieldLabel(k)}</span>
              </label>
              <div className="col-span-7 min-w-0">
                <input
                  className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none"
                  placeholder={predV != null ? `PRED: ${predV}` : '(없음)'}
                  value={editV == null ? '' : String(editV)}
                  onChange={e => setEditField(k, e.target.value)}
                />
                {predV != null && String(predV) !== String(editV ?? '') && (
                  <button
                    type="button"
                    onClick={() => copyPredToGt(k)}
                    className="mt-0.5 text-[10px] text-blue-600 hover:text-blue-800 truncate block w-full text-left"
                  >
                    ← PRED 복사: {String(predV)}
                  </button>
                )}
                {showFilterDiff && (
                  <div className="text-[10px] text-gray-500 mt-0.5 flex items-center gap-1">
                    <span className={`w-1 h-1 rounded-full ${dotColor(statusF)}`} />
                    필터: {predFV == null ? '(차단)' : String(predFV)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 메모 */}
      <div className="px-4 py-2.5 border-t bg-gray-50">
        <textarea
          className="w-full text-xs px-2 py-1.5 border border-gray-200 rounded resize-none focus:border-gray-500 outline-none bg-white"
          rows={2}
          placeholder="메모 (수정 사유, 선택)"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
      </div>

      {/* 버튼 */}
      <div className="px-4 py-3 border-t flex justify-end gap-2 bg-white">
        <button onClick={onClose} className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 text-gray-700">
          닫기
        </button>
        <button
          onClick={save}
          disabled={saving || !editGt}
          className="px-4 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 flex items-center gap-1.5 font-medium shadow-sm"
        >
          <Save className="w-3.5 h-3.5" /> {saving ? '저장중' : '수정 GT 저장'}
        </button>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// 좌측 리스트 (선택 시 가운데+우측 패널 펼쳐짐)
// ──────────────────────────────────────────────────────────
function SampleRow({ it, selected, onClick, compact }) {
  const isFull = it.has_pred && it.match_count === it.total_fields;
  const isNone = it.has_pred && it.match_count === 0;
  const matchPct = it.match_pct == null ? 0 : it.match_pct;
  const ocrPct   = it.ocr_match_pct == null ? 0 : it.ocr_match_pct;
  const filtPct  = it.match_pct_filtered == null ? 0 : it.match_pct_filtered;

  const Icon =
    !it.has_pred ? null
    : isFull ? CheckCircle
    : isNone ? XCircle
    : AlertTriangle;
  const iconColor =
    !it.has_pred ? ''
    : isFull ? 'text-gray-700'
    : isNone ? 'text-rose-500'
    : 'text-gray-400';

  if (compact) {
    return (
      <div
        onClick={onClick}
        className={`px-3 py-2 border-b border-gray-100 cursor-pointer transition-colors ${
          selected ? 'bg-blue-50 border-l-2 border-l-blue-500 -ml-px pl-[11px]' : 'hover:bg-gray-50'
        }`}
      >
        <div className="flex items-center gap-2 mb-1">
          {Icon ? <Icon className={`w-3.5 h-3.5 ${iconColor}`} /> : <span className="w-3.5 h-3.5 inline-block" />}
          <span className="text-xs font-mono text-gray-800 truncate flex-1">{it.id}</span>
          <span className={`text-[10px] px-1 py-0 rounded ${it.split === 'val' ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-500'}`}>
            {it.split}
          </span>
        </div>
        <div className="flex items-center gap-2 ml-5.5 text-[10px] text-gray-500">
          <span className="truncate">{it.doc_type_nm || it.doc_type}</span>
          {it.has_pred && (
            <>
              <span className="ml-auto tabular-nums">{it.match_count}/{it.total_fields}</span>
              <div className="w-12 h-0.5 bg-gray-100 rounded overflow-hidden">
                <div className="h-full bg-blue-500" style={{ width: `${matchPct * 100}%` }} />
              </div>
            </>
          )}
          {it.vin_invalid && <span className="text-rose-600 font-medium">VIN</span>}
        </div>
      </div>
    );
  }

  // wide table row (선택 안 됐을 때)
  return (
    <tr
      onClick={onClick}
      className={`border-b border-gray-100 cursor-pointer transition-colors ${selected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
    >
      <td className="px-3 py-2">
        {Icon ? <Icon className={`w-4 h-4 ${iconColor}`} /> : <span className="w-3 h-3 inline-block" />}
      </td>
      <td className="px-3 py-2 font-mono text-xs text-gray-800">{it.id}</td>
      <td className="px-2 py-2">
        <span className="text-[11px] px-2 py-0.5 rounded bg-gray-100 text-gray-700">
          {it.doc_type_nm || it.doc_type || '?'}
        </span>
      </td>
      <td className="px-2 py-2">
        <span className={`text-[10px] px-1.5 py-0.5 rounded ${it.split === 'val' ? 'bg-blue-100 text-blue-700' : 'bg-gray-50 text-gray-500'}`}>
          {it.split}
        </span>
      </td>
      <td className="px-3 py-2 text-xs">
        {it.has_pred ? (
          <div>
            <div className="text-gray-900 tabular-nums font-medium">{it.match_count}/{it.total_fields}</div>
            <div className="w-full h-0.5 bg-gray-100 rounded mt-1 overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${matchPct * 100}%` }} />
            </div>
          </div>
        ) : <span className="text-gray-300">-</span>}
      </td>
      <td className="px-3 py-2 text-xs">
        {it.has_pred ? (
          <div>
            <div className="text-gray-700 tabular-nums">{pct(ocrPct)}</div>
            <div className="w-full h-0.5 bg-gray-100 rounded mt-1 overflow-hidden">
              <div className="h-full bg-blue-300" style={{ width: `${ocrPct * 100}%` }} />
            </div>
          </div>
        ) : <span className="text-gray-300">-</span>}
      </td>
      <td className="px-3 py-2 text-xs">
        {it.has_pred ? (
          <div>
            <div className="text-gray-700 tabular-nums">{pct(filtPct)}</div>
            <div className="w-full h-0.5 bg-gray-100 rounded mt-1 overflow-hidden">
              <div className="h-full bg-blue-500" style={{ width: `${filtPct * 100}%` }} />
            </div>
          </div>
        ) : <span className="text-gray-300">-</span>}
      </td>
      <td className="px-3 py-2 text-xs">
        {it.vin_invalid ? <span className="text-rose-600 font-semibold">불량</span> : null}
      </td>
    </tr>
  );
}

// ──────────────────────────────────────────────────────────
// 메인 패널
// ──────────────────────────────────────────────────────────
export default function TrainingVerifyExportPanel() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);

  const [docType, setDocType] = useState('ALL');
  const [match, setMatch] = useState('');
  const [split, setSplit] = useState('all');
  const [adapter, setAdapter] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);

  // corrections 누적 카운트 + sync 상태
  const [corrCounts, setCorrCounts] = useState({ vision_count: 0, text_count: 0, legacy_count: 0 });
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  const reloadCorrCounts = useCallback(async () => {
    try {
      const r = await fetch('/api/export-eval/corrections?adapter=all');
      const j = await r.json();
      setCorrCounts({
        vision_count: j.vision_count || 0,
        text_count:   j.text_count   || 0,
        legacy_count: j.legacy_count || 0,
      });
    } catch (e) { /* ignore */ }
  }, []);

  const syncToServer = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const r = await fetch('/api/export-eval/sync-to-server', { method: 'POST' });
      const j = await r.json();
      setLastSync(j);
      if (j.ok) {
        const v = j.vision?.pushed ?? 0;
        const t = j.text?.pushed ?? 0;
        alert(`서버 동기화 완료\nvision: ${v}건\ntext: ${t}건`);
      } else {
        const errs = [];
        if (j.vision && !j.vision.ok) errs.push(`vision: ${j.vision.error}`);
        if (j.text   && !j.text.ok)   errs.push(`text: ${j.text.error}`);
        alert(`동기화 실패\n${errs.join('\n')}`);
      }
    } catch (e) {
      alert(`동기화 오류: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const reloadStats = useCallback(async () => {
    try {
      const r = await fetch('/api/export-eval/stats');
      const j = await r.json();
      setStats(j);
    } catch (e) { console.error(e); }
  }, []);

  const reloadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page), pageSize: String(pageSize), split, adapter,
      });
      if (docType && docType !== 'ALL') params.set('type', docType);
      if (match) params.set('match', match);
      if (q) params.set('q', q);
      const r = await fetch(`/api/export-eval/samples?${params}`);
      const j = await r.json();
      setItems(j.items || []);
      setTotal(j.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [page, pageSize, split, adapter, docType, match, q]);

  useEffect(() => { reloadStats(); }, [reloadStats]);
  useEffect(() => { reloadList(); }, [reloadList]);
  useEffect(() => { reloadCorrCounts(); }, [reloadCorrCounts]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    fetch(`/api/export-eval/samples/${encodeURIComponent(selectedId)}`)
      .then(r => r.json()).then(setDetail).catch(console.error);
  }, [selectedId]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const reloadCache = async () => {
    await fetch('/api/export-eval/reload', { method: 'POST' });
    await Promise.all([reloadStats(), reloadList()]);
  };

  const typeCountMap = {};
  for (const tc of (stats?.type_counts || [])) typeCountMap[tc.type_cd] = tc.total;

  const detailOpen = !!selectedId;
  const clearSelection = () => { setSelectedId(null); setDetail(null); };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* ─── 상단 ─── */}
      <div className="px-5 pt-4 pb-2.5 border-b">
        <div className="flex items-center justify-between mb-2.5">
          <h3 className="text-base font-semibold text-gray-900">학습데이타검증 (수출)</h3>
          <div className="flex items-center gap-2">
            {/* corrections 누적 + 서버 sync */}
            {(corrCounts.vision_count + corrCounts.text_count + corrCounts.legacy_count) > 0 && (
              <span className="text-xs text-gray-600">
                수정 누적{' '}
                <b className="text-blue-700">{corrCounts.vision_count}</b>(vision)
                {corrCounts.text_count > 0 && <> · <b className="text-blue-700">{corrCounts.text_count}</b>(text)</>}
                {corrCounts.legacy_count > 0 && <> · <span className="text-gray-400">{corrCounts.legacy_count}(legacy)</span></>}
              </span>
            )}
            <button
              onClick={syncToServer}
              disabled={syncing || (corrCounts.vision_count + corrCounts.text_count) === 0}
              className="text-xs px-2.5 py-1 rounded border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-40 flex items-center gap-1"
              title="로컬 corrections 를 학습 서버로 push"
            >
              {syncing
                ? <><CloudOff className="w-3 h-3" /> 동기화중...</>
                : <><Cloud className="w-3 h-3" /> 서버 sync</>}
            </button>
            <button onClick={reloadCache} className="text-xs px-2.5 py-1 rounded border border-gray-300 hover:bg-gray-50 flex items-center gap-1 text-gray-700">
              <RotateCcw className="w-3 h-3" /> 캐시 reload
            </button>
          </div>
        </div>
        {stats && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
            <span>전체 <b className="text-gray-900">{stats.total_samples}</b></span>
            <span>val <b className="text-blue-700">{stats.val_count}</b> / train <b className="text-gray-900">{stats.train_count}</b></span>
            <span>필드 일치 <b className="text-blue-700">{pct(stats.overall_accuracy)}</b></span>
            <span>OCR 일치 <b className="text-gray-900">{pct(stats.overall_ocr_accuracy)}</b></span>
            <span>필터적용 <b className="text-blue-700">{pct(stats.overall_accuracy_filtered)}</b></span>
          </div>
        )}
      </div>

      {/* ─── doc_type 탭 (회색 톤, 활성만 강조) ─── */}
      <div className="px-5 py-2 border-b flex flex-wrap gap-1">
        {DOC_TYPES.map(d => {
          const active = docType === d.code;
          const cnt = d.code === 'ALL' ? (stats?.total_samples ?? 0) : (typeCountMap[d.code] ?? 0);
          return (
            <button
              key={d.code}
              onClick={() => { setDocType(d.code); setPage(1); }}
              className={`px-2.5 py-1 text-xs rounded-md font-medium transition-colors ${
                active
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
              }`}
            >
              {d.name}
              {cnt > 0 && (
                <span className={`ml-1.5 text-[10px] tabular-nums ${active ? 'text-blue-100' : 'text-gray-500'}`}>
                  {cnt}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ─── 보조 필터 ─── */}
      <div className="px-5 py-2 border-b flex flex-wrap gap-2 items-center">
        <select value={split} onChange={e => { setSplit(e.target.value); setPage(1); }} className="text-xs px-2 py-1 border border-gray-200 rounded">
          {SPLIT_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={adapter} onChange={e => { setAdapter(e.target.value); setPage(1); }} className="text-xs px-2 py-1 border border-gray-200 rounded">
          {ADAPTER_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={match} onChange={e => { setMatch(e.target.value); setPage(1); }} className="text-xs px-2 py-1 border border-gray-200 rounded">
          {MATCH_FILTERS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <div className="ml-auto flex items-center gap-1">
          <Search className="w-3.5 h-3.5 text-gray-400" />
          <input
            value={qInput}
            onChange={e => setQInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { setQ(qInput); setPage(1); } }}
            placeholder="검색 (id, 필드값)"
            className="text-xs px-2 py-1 border border-gray-200 rounded w-56 focus:border-blue-500 focus:ring-1 focus:ring-blue-100 outline-none"
          />
          <button onClick={() => { setQ(qInput); setPage(1); }} className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">검색</button>
        </div>
      </div>

      {/* ─── 본문: 리스트 + 가운데 뷰어 + 우측 폼 ─── */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* 좌측 리스트 */}
        <div className={`${detailOpen ? 'w-[300px]' : 'flex-1'} flex flex-col border-r min-w-0`}>
          <div className="flex-1 overflow-auto">
            {loading ? (
              <div className="p-8 text-sm text-gray-400 text-center">로딩...</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-sm text-gray-400 text-center">결과 없음</div>
            ) : detailOpen ? (
              // compact 리스트 (선택 시)
              <div>
                {items.map(it => (
                  <SampleRow
                    key={it.id} it={it} compact
                    selected={selectedId === it.id}
                    onClick={() => setSelectedId(it.id)}
                  />
                ))}
              </div>
            ) : (
              // 와이드 테이블 (선택 안 됐을 때)
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left text-gray-600">
                    <th className="px-3 py-2 w-10"></th>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-2 py-2 w-24">유형</th>
                    <th className="px-2 py-2 w-14">split</th>
                    <th className="px-3 py-2 w-28">필드일치</th>
                    <th className="px-3 py-2 w-24">OCR</th>
                    <th className="px-3 py-2 w-24">필터적용</th>
                    <th className="px-3 py-2 w-16">VIN</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <SampleRow
                      key={it.id} it={it}
                      selected={selectedId === it.id}
                      onClick={() => setSelectedId(it.id)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
          {/* 페이지네이션 */}
          <div className="px-3 py-2 border-t flex items-center justify-between text-xs text-gray-700 bg-white">
            <div>총 <b>{total}</b>건 · {page}/{totalPages}p</div>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">이전</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}
                className="px-2 py-1 rounded border border-gray-300 disabled:opacity-30 hover:bg-gray-50">다음</button>
            </div>
          </div>
        </div>

        {/* 가운데 이미지 뷰어 (선택 시만) */}
        {detailOpen && (
          <ImageViewer
            urls={detail?.image_urls || []}
            sourceFilename={detail?.source_filename}
            xlsxUrl={detail?.xlsx_url}
            onClose={clearSelection}
          />
        )}

        {/* 우측 폼 (선택 시만) */}
        {detailOpen && detail && (
          <FormPanel
            sample={detail}
            onClose={clearSelection}
            onClearSelection={clearSelection}
            onSaved={() => { reloadStats(); reloadCorrCounts(); }}
          />
        )}
      </div>
    </div>
  );
}
